import React, { useState } from "react";
import {
  Box,
  CssBaseline,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PeopleIcon from "@mui/icons-material/People";
import EngineeringIcon from "@mui/icons-material/Engineering";

import { Link, Outlet, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { productsActions, sessionActions, usersActions } from "../store/";

const drawerWidth = 260;

/* -------------------------------------------------------
   GLASS MODERN DESIGN MIXINS
------------------------------------------------------- */
const openedMixin = (theme) => ({
  width: drawerWidth,
  background: "rgba(12, 32, 61, 1)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  boxShadow: "8px 0 32px rgba(0,0,0,0.45)",
  borderRight: "1px solid rgba(255,255,255,0.12)",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: 300,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  background: "rgba(10, 25, 47, 0.98)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  borderRight: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "4px 0 24px rgba(0,0,0,0.45)",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: 260,
  }),
  width: `calc(${theme.spacing(7)} + 1px)`,
  overflowX: "hidden",
  [theme.breakpoints.up("sm")]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  color: "#fff",
  ...theme.mixins.toolbar,
}));

/* -------------------------------------------------------
   APP BAR MODERNE
------------------------------------------------------- */
const AppBar = styled(MuiAppBar, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    height: 72,
    display: "flex",
    justifyContent: "center",
    background: "linear-gradient(90deg, #002355 0%, #0082fb 100%)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
    borderBottom: "1px solid rgba(255,255,255,0.15)",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.easeInOut,
      duration: 350,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    }),
  })
);

/* -------------------------------------------------------
   DRAWER MODERNE
------------------------------------------------------- */
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    "& .MuiDrawer-paper": {
      border: "none",
      borderRadius: "0 0 20px 0",
      color: "#e3f2fd",
      ...(open ? openedMixin(theme) : closedMixin(theme)),
    },
  })
);

export default function MiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const _userRole = useSelector((state) => state.session.user?.role);
  const _username = useSelector((state) => state.session.user?.email || "Technicien");

  const logout = () => {
    localStorage.removeItem("TOKEN");
    navigate("/login");
    dispatch(sessionActions.updateUser({ user: {} }));
    dispatch(productsActions.setItems([]));
    dispatch(usersActions.setItems([]));
  };

  /* -------------------------------------------------------
     MENU ITEMS (same logic as your original code)
  ------------------------------------------------------- */
  const menuItems = [
    { name: "Dashboard", icon: <DashboardIcon />, link: "/dashboard" },
    { name: "Maintenances", icon: <EngineeringIcon />, link: "/settings/maintenances" },
        { name: "Stats", icon: <EngineeringIcon />, link: "/settings/stats" },

  ];

  if (_userRole === "superadmin" || _userRole === "admin") {
    menuItems.push(
      { name: "Produits", icon: <PrecisionManufacturingIcon />, link: "/settings/products" },
      { name: "Clients", icon: <PeopleIcon />, link: "/settings/clients" },
      { name: "Utilisateurs", icon: <PeopleOutlineIcon />, link: "/settings/users" }
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "linear-gradient(145deg,#f5f7fb,#e3edf9)" }}>
      <CssBaseline />

      {/* APPBAR */}
      <AppBar position="fixed" open={open}>
        <Toolbar sx={{ px: 2 }}>
          <IconButton color="inherit" onClick={() => setOpen(true)}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
            🚍 Plateforme Maintenance Transport
          </Typography>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 500 }}>{_username}</Typography>
            <Tooltip title="Se déconnecter">
              <IconButton sx={{ color: "white" }} onClick={logout}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* DRAWER */}
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <Fade in={open}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Maintenance
            </Typography>
          </Fade>

          <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}>
            {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

        <List>
          {menuItems.map((item) => (
            <ListItem key={item.name} disablePadding component={Link} to={item.link}>
              <ListItemButton
                sx={{
                  mx: 1,
                  my: 0.7,
                  borderRadius: "12px",
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: "#e3f2fd",
                  "&:hover": {
                    background: "linear-gradient(90deg,#0066ff,#00b8ff)",
                    transform: "scale(1.03)",
                    transition: "0.25s",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={item.name}
                  sx={{ opacity: open ? 1 : 0, transition: "0.2s" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* MAIN CONTENT */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
}
